<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import Message from 'primevue/message'
import { useCommerceStore } from '~/stores/pos/commerce'

const commerceStore = useCommerceStore()
const {
  itemsInCart,
  hasClientDetails,
  compactViewEnabled,
  cartTotal,
  selectedDiscount,
  paymentMethodSurchargePercentage,
  isEditingOrder,
  editingOrderId,
  details,
  cashReceived,
  calculatedChange,
} = storeToRefs(commerceStore)
const { clearCart, clearDetails, cancelOrderEdit, resetCashCalculation } = commerceStore

const showCalculator = ref(false)

function toggleDelivery() {
  details.value.delivery = !details.value.delivery
}
</script>

<template>
  <div class="h-full flex flex-col overflow-y-auto">
    <!-- Order Editing Message -->
    <Message
      v-if="isEditingOrder"
      severity="warn"
      :closable="false"
      class="mb-2"
      pt:root:class="w-full"
      pt:content:style="display: block"
    >
      <div class="flex items-center justify-between">
        <div class="flex flex-grow-1 items-center space-x-3">
          <i class="pi pi-pencil" />
          <span class="text-sm">{{ $t('You are editing existing order') }} #{{ editingOrderId?.slice(-4) }}</span>
          <NuxtLink
            :to="`/app/orders/${editingOrderId}`"
            class="underline text-blue-600 hover:text-blue-800 text-sm"
          >
            {{ $t('View original order') }}
          </NuxtLink>
        </div>
        <Button
          :label="$t('Cancel Editing')"
          severity="secondary"
          size="small"
          icon="pi pi-times"
          @click="cancelOrderEdit"
        />
      </div>
    </Message>

    <div>
      <div class="flex gap-1 pb-1 flex-wrap">
        <PublicStoreOrderUserData />

        <Button
          :disabled="!hasClientDetails"
          :label="$t('Clear client')"
          icon="pi pi-refresh"
          size="small"
          severity="warn"
          @click="clearDetails"
        />
        <Button
          :disabled="itemsInCart < 1"
          :label="$t('Clear cart')"
          icon="pi pi-trash"
          size="small"
          @click="clearCart"
        />
        <Button
          :label="$t('To go')"
          icon="pi pi-sign-in"
          size="small"
          severity="secondary"
          @click="toggleDelivery"
        />
        <Button
          :icon="compactViewEnabled ? 'pi pi-list' : 'pi pi-align-justify'"
          severity="secondary"
          text
          size="small"
          @click="compactViewEnabled = !compactViewEnabled"
        />
      </div>
      <PublicStoreOrderUserDetails />
    </div>
    <div
      v-if="itemsInCart > 0"
      class="flex-1 overflow-y-auto bg-gray-50/50 min-h-[160px] md:min-h-[240px]"
    >
      <PublicStoreCartReviewDetails :compact-view="compactViewEnabled" />
    </div>
    <!-- Spacer to push actions to bottom when cart is empty -->
    <div
      v-else
      class="flex-1 bg-gray-50/50"
    />
    <div
      id="actions"
      class="flex flex-col w-full mt-auto"
    >
      <div
        v-if="selectedDiscount && selectedDiscount.percentage"
        class="w-full bg-red-400 text-base"
      >
        <div class="p-2">
          <span>{{ $t('Applied discount') }}</span>
          <span class="font-semibold">  %{{ selectedDiscount.percentage }}</span>
        </div>
      </div>
      <div
        v-if="paymentMethodSurchargePercentage && paymentMethodSurchargePercentage > 0"
        class="w-full bg-red-400 text-base"
      >
        <div class="p-2">
          <span>{{ $t('Surcharge percentage') }}</span>
          <span class="font-semibold">  %{{ paymentMethodSurchargePercentage }}</span>
        </div>
      </div>
      <div class="p-1 border-t-2 border-gray-200 bg-white">
        <!-- Total Display with Calculator Button -->
        <div class="flex items-center gap-1 mb-2">
          <Button
            icon="pi pi-calculator"
            severity="info"
            size="large"
            class="flex-shrink-0"
            @click="showCalculator = true"
          />
          <Button
            v-if="cashReceived !== null"
            label="C"
            severity="contrast"
            text
            size="large"
            class="flex-shrink-0"
            @click="resetCashCalculation"
          />
          <div class="flex-1 bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-lg border border-gray-200">
            <div class="flex justify-between items-center">
              <span class="text-lg font-semibold text-gray-700">{{ $t('Total') }}:</span>
              <span class="text-2xl font-bold text-emerald-600">{{ usePriceFormatter(Number(cartTotal)) }}</span>
            </div>
            <div
              v-if="cashReceived !== null && calculatedChange !== null"
              class="mt-2 pt-2 border-t border-gray-300"
            >
              <div class="flex justify-between items-center text-sm mb-1">
                <span class="text-gray-600">{{ $t('Cash Received') }}:</span>
                <span class="font-semibold text-blue-600">{{ usePriceFormatter(cashReceived) }}</span>
              </div>
              <div class="flex justify-between items-center text-sm">
                <span class="text-gray-600">{{ $t('Change') }}:</span>
                <span class="font-bold text-emerald-600">{{ usePriceFormatter(calculatedChange) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex gap-2 mx-2">
        <div class="flex-1">
          <CartSelectPaymentMethod />
        </div>
        <div class="flex-1">
          <CartDiscounts />
        </div>
      </div>
      <CartGenerateOrderButton />
    </div>

    <!-- Change Calculator Dialog -->
    <CartChangeCalculator v-model:visible="showCalculator" />
  </div>
</template>
